import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, FileDown, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";

const ACTION_STYLE = { create: "text-cyan-300", edit: "text-violet-300", close: "text-amber-300", import: "text-emerald-300" };

export default function AuditLog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [log, setLog] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const allowed = user && ["coordinator", "manager", "admin"].includes(user.role);
  useEffect(() => { if (allowed) api.get("/audit-log").then((r) => setLog(r.data)).catch(() => setLog([])); }, [allowed]);

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.get("/audit-log/verify");
      setIntegrity(data);
      if (data.ok) toast.success(`Audit trail intact · ${data.count} entries verified`);
      else toast.error(`Tampering detected at entry #${data.broken_at_seq}`);
    } catch { toast.error("Could not verify audit trail"); }
    finally { setVerifying(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/audit-log/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "glh_audit_trail.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (user && !allowed) return <Navigate to="/" replace />;

  return (
    <div className="p-6 space-y-4" data-testid="audit-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head font-black text-3xl tracking-tight">Audit Log</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">{log.length} entries · tamper-evident (SHA-256 hash-chained) · who + when (HIPAA)</p>
        </div>
        <div className="flex items-center gap-2">
          {integrity && (
            <span data-testid="integrity-badge" className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-mono border ${integrity.ok ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" : "text-red-300 border-red-500/30 bg-red-500/10"}`}>
              {integrity.ok ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
              {integrity.ok ? `Verified · ${integrity.count}` : `Broken @ #${integrity.broken_at_seq}`}
            </span>
          )}
          <button onClick={verify} disabled={verifying} data-testid="verify-integrity-btn" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 hover:bg-white/5 px-3 py-1.5 text-xs font-mono transition-colors disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${verifying ? "animate-spin" : ""}`} /> Verify integrity
          </button>
          <button onClick={exportCsv} data-testid="export-audit-btn" className="inline-flex items-center gap-1.5 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-3 py-1.5 text-xs transition-colors">
            <FileDown className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="glass rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono border-b border-white/8">
            <th className="text-left px-3 py-2.5">When</th><th className="text-left px-2 py-2.5">Action</th><th className="text-left px-2 py-2.5">Referral</th><th className="text-left px-2 py-2.5">User</th><th className="text-left px-3 py-2.5">Changes / Note</th>
          </tr></thead>
          <tbody>
            {log.map((e, i) => (
              <tr key={i} className="row-hover border-b border-white/5 last:border-0">
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(e.at)}</td>
                <td className={`px-2 py-2 font-mono text-xs uppercase font-semibold ${ACTION_STYLE[e.action] || ""}`}>{e.action}</td>
                <td className="px-2 py-2">
                  <button onClick={() => navigate(`/referrals/${e.referral_id}`)} className="font-mono text-cyan-300 text-xs hover:underline" data-testid={`audit-ref-${e.referral_id}`}>{e.referral_id}</button>
                </td>
                <td className="px-2 py-2 text-xs whitespace-nowrap">{e.user_name} <span className="text-muted-foreground">({e.role})</span></td>
                <td className="px-3 py-2 text-xs font-mono text-slate-400">
                  {e.changes && Object.keys(e.changes).length > 0
                    ? Object.entries(e.changes).map(([k, v]) => <span key={k} className="mr-2">{k}: {String(v.from ?? "∅")}→{String(v.to ?? "∅")}</span>)
                    : (e.note || "—")}
                </td>
              </tr>
            ))}
            {log.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground font-mono text-xs">No audit entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
